import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import type { Article } from "@newsfeed/shared";
import { formatDate } from "../format.js";
import { sharedStyles } from "../styles.js";
import "./favicon.js";

/**
 * One article in a list. Dumb/presentational: it dispatches intent events and
 * the containing view performs the API calls.
 *
 * Events (bubbling, composed), each with detail `{ article }`:
 *   - `open-article`  : title clicked. Opens in a new tab natively (anchor
 *                       target=_blank rel=noopener) AND signals the view to call
 *                       openArticle, which marks the article read and clears the
 *                       `updated` badge server-side.
 *   - `toggle-read`   : detail also carries `{ read }` = the desired next state.
 *   - `toggle-keep`   : detail also carries `{ kept }` = the desired next state.
 */
export class NfArticleRow extends LitElement {
  static override properties = {
    article: { attribute: false },
    pending: { attribute: false },
  };

  article!: Article;
  /** Which state-change action is in flight for this row: "read", "keep", or none. */
  pending: "read" | "keep" | null = null;

  static override styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        border-bottom: 1px solid var(--nf-border);
      }
      .item {
        display: flex;
        gap: var(--nf-space-12);
        padding: var(--nf-space-12) var(--nf-space-4);
        align-items: flex-start;
      }
      .item__main {
        flex: 1;
        min-width: 0;
      }
      .item__title {
        text-decoration: none;
        color: var(--nf-text);
        font-weight: 400;
      }
      .item--unread .item__title {
        font-weight: 700;
      }
      .item--read .item__title {
        color: var(--nf-muted);
      }
      .item__title:hover {
        color: var(--nf-accent);
      }
      .item__meta {
        font-size: var(--nf-font-13);
        margin-top: var(--nf-space-2);
        display: flex;
        gap: var(--nf-space-4);
        flex-wrap: wrap;
      }
      .item__actions {
        display: flex;
        gap: var(--nf-space-6);
        flex-shrink: 0;
      }
      .item__icon {
        margin-top: var(--nf-space-2);
      }
      .badge {
        margin-left: var(--nf-space-8);
      }
    `,
  ];

  private emit(type: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(
      new CustomEvent(type, { detail, bubbles: true, composed: true }),
    );
  }

  override render(): TemplateResult {
    const a = this.article;
    const unread = a.readState === "unread";
    return html`
      <div class="item ${unread ? "item--unread" : "item--read"}">
        <nf-favicon
          class="item__icon"
          .url=${a.feedFaviconUrl}
          .label=${a.feedTitle}
        ></nf-favicon>
        <div class="item__main">
          <a
            class="item__title"
            href=${a.link}
            target="_blank"
            rel="noopener noreferrer"
            @click=${() => this.emit("open-article", { article: a })}
          >
            ${a.title}
          </a>
          ${a.updated ? html`<span class="badge">updated</span>` : nothing}
          <div class="item__meta muted">
            <span>${a.feedTitle}</span>
            ${a.author ? html`<span>· ${a.author}</span>` : nothing}
            <span>· ${formatDate(a.effectiveDate)}</span>
          </div>
        </div>
        <div class="item__actions">
          <button
            title=${unread ? "Mark read" : "Mark unread"}
            ?disabled=${this.pending === "read"}
            @click=${() => this.emit("toggle-read", { article: a, read: unread })}
          >
            ${this.pending === "read" ? html`<span class="spinner"></span>` : nothing}
            ${unread ? "Mark read" : "Mark unread"}
          </button>
          <button
            class=${a.kept ? "primary" : ""}
            title=${a.kept ? "Remove from kept" : "Keep"}
            ?disabled=${this.pending === "keep"}
            @click=${() => this.emit("toggle-keep", { article: a, kept: !a.kept })}
          >
            ${this.pending === "keep" ? html`<span class="spinner"></span>` : nothing}
            ${a.kept ? "Kept" : "Keep"}
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define("nf-article-row", NfArticleRow);

declare global {
  interface HTMLElementTagNameMap {
    "nf-article-row": NfArticleRow;
  }
}
